import React, { useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';

import GameModal from '../GameModal/GameModal';
import { withGetStringAudio } from 'components/hocs';
import { closeActivityHelp } from 'actions/gameActions';
import { toggleAudio, stopAudio } from 'actions/audioActions';
import { s3bucketPublicURL, imagesURL } from 'config';
import { formatHtmlForPlay, getTranslateFunc, isMobileAppRuntime } from 'utils';
import { resolveActivityHelpLocale, resolveActivityHelpContent } from './resolveActivityHelp';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { inspectReadingString, logReadingLocale, isReadingLocaleDebugEnabled } from 'utils/readingLocaleDiagnostics';
import mobileClasses from './mobile/ActivityHelpMobile.module.scss';

const ActivityHelp = ({
  audioSrc,
  isPlaying,
  strings,
  currentActivity,
  closeActivityHelp,
  solutionLocale,
  activityLocale,
  getStringAudio,
  versionType,
  placementLocaleCode,
  language,
}) => {
  const {
    data,
    template,
  } = currentActivity;

  const { multiLocaleHelp, helpLocale } = data;
  const mobileRuntime = isMobileAppRuntime();
  const androidRuntime = mobileRuntime && getMobileAppPlatform() === 'android';
  const isReadingDeluxe = String(template?.alias || '')
    .toUpperCase().replace(/-/g, '_') === 'READING_DELUXE';
  const interfaceLocaleCode = solutionLocale?.code || 'en';
  // The wrapper supplies this resolved locale to both withGetStringAudio and
  // the text translator. Never choose different languages for Help and audio.
  const activityHelpLocaleCode = language || resolveActivityHelpLocale({
    currentActivity, versionType, placementLocaleCode,
    activityLocale, solutionLocale, helpLocale,
  });
  const readingTextLocaleCode = isReadingDeluxe ? activityHelpLocaleCode : '';
  const translate = useMemo(() => getTranslateFunc(strings, activityHelpLocaleCode), [
    strings, activityHelpLocaleCode,
  ]);
  const resolvedHelp = resolveActivityHelpContent({
    data, template, currentActivity, versionType,
    locale: activityHelpLocaleCode, strings, translate, getStringAudio,
    s3bucketPublicURL, mobileRuntime,
  });
  const {
    helpString, helpHtml, helpAudio, audioHelpUrl, hasLocalizedHelpHtml,
    hasLocalizedHelpAudio, templateWordlistHelpString, shouldUseTemplateWordlistHelpString,
  } = resolvedHelp;
  const helpLocaleCandidates = useMemo(() => (multiLocaleHelp
    ? [activityHelpLocaleCode, multiLocaleHelp.defaultLocale, 'en']
      .filter((code, index, codes) => code && codes.indexOf(code) === index)
    : []), [multiLocaleHelp, activityHelpLocaleCode]);
  if (process.env.NODE_ENV !== 'production'
      && (resolvedHelp.textSource === 'base-fallback'
        || resolvedHelp.audioSource === 'missing'
        || resolvedHelp.audioSource === 'legacy-vm-base-unverified')) {
    console.warn('[ActivityHelp][localization]', {
      activityId: currentActivity?.activity?._id || '',
      locale: activityHelpLocaleCode,
      textSource: resolvedHelp.textSource,
      audioSource: resolvedHelp.audioSource,
      helpString,
    });
  }

  console.log('[ActivityHelp][resolveHelp]', {
    activityId: currentActivity?.activity?._id || '',
    templateAlias: template?.alias || '',
    isGenerated: !!data?.isGenerated,
    storedHelpString: data?.helpString || '',
    templateWordlistHelpString: templateWordlistHelpString || '',
    shouldUseTemplateWordlistHelpString,
    resolvedHelpString: helpString || '',
    interfaceLocale: interfaceLocaleCode,
    selectedReadingTextLocale: readingTextLocaleCode,
    activityHelpLocale: activityHelpLocaleCode,
    authoredLocalizedHelpHtml: hasLocalizedHelpHtml,
    authoredLocalizedHelpAudio: hasLocalizedHelpAudio,
    placementLocale: placementLocaleCode || '',
    versionType: versionType || '',
    audioHelpUrl: audioHelpUrl || '',
    textSource: resolvedHelp.textSource,
    audioSource: resolvedHelp.audioSource,
  });

  const audioIcon = `${imagesURL}/Images/Audio/${audioSrc === audioHelpUrl && isPlaying ? 'audiostop' : 'audio_icon1'
    }.png`;

  const closeModal = useCallback(() => {
    closeActivityHelp();
    stopAudio();
  }, [closeActivityHelp]);

  useEffect(() => {
    if (!audioHelpUrl) return;
    toggleAudio(audioHelpUrl, { onSuccessEnd: closeModal });
  }, [audioHelpUrl, closeModal]);

  useEffect(() => {
    const keyDownHandler = (e) => {
      if (e.keyCode === 13 || e.keyCode === 27) {
        e.preventDefault();
        closeModal();
      }
    };

    window.addEventListener('keydown', keyDownHandler);

    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  }, [closeModal]);

  const formattedHtml = useMemo(() => formatHtmlForPlay({ html: helpHtml }), [
    helpHtml,
  ]);

  // Observe the exact authored, translated, and displayed Help values in
  // Preview and Play without changing selection, audio, or fallback logic.
  useEffect(() => {
    if (!isReadingLocaleDebugEnabled()) return;
    const alias = String(template?.alias || '').toUpperCase().replace(/-/g, '_');
    if (alias !== 'READING_DELUXE') return;
    logReadingLocale('help-render', {
      activityId: currentActivity?.activity?._id || null,
      templateAlias: template?.alias || null,
      versionType: versionType || '(base)',
      uiLocale: solutionLocale?.code || null,
      activityLocale: activityLocale?.code || null,
      placementLocale: placementLocaleCode || null,
      propAudioLanguage: language || null,
      configuredHelpLocale: helpLocale || null,
      resolvedHelpLocale: activityHelpLocaleCode,
      readingTextLocale: readingTextLocaleCode,
      isReadingDeluxePlayRuleActive: isReadingDeluxe,
      authored: {
        helpString: data?.helpString ?? null,
        helpHtml: data?.helpHtml ?? null,
        helpAudio: data?.helpAudio ?? null,
        multiLocaleHelp: multiLocaleHelp || null,
        generated: !!data?.isGenerated,
        templateWordlistHelpString: templateWordlistHelpString || null,
      },
      helpLocaleCandidates,
      chosenHelpString: helpString ?? null,
      commonString: inspectReadingString(strings, helpString, readingTextLocaleCode || activityHelpLocaleCode),
      chosenTextBeforeFormatting: helpHtml ?? null,
      renderedHtml: formattedHtml ?? null,
      chosenAudioFile: helpAudio || null,
      chosenAudioUrl: audioHelpUrl || null,
      audioPlaying: !!isPlaying,
    });
  }, [
    currentActivity, template, data, versionType, solutionLocale, activityLocale,
    placementLocaleCode, language, helpLocale, activityHelpLocaleCode,
    readingTextLocaleCode, isReadingDeluxe, multiLocaleHelp,
    templateWordlistHelpString, helpString, helpHtml, formattedHtml,
    helpAudio, audioHelpUrl, isPlaying, strings, helpLocaleCandidates
  ]);

  return (
    <GameModal
      title={translate(66, 'Help')}
      {...(mobileRuntime
        ? {
          outerClassnames: [
            mobileClasses.modalOuter,
            ...(androidRuntime ? [mobileClasses.modalOuterAndroid] : []),
          ],
          innerClassnames: [mobileClasses.modalInner],
          contentClassnames: [mobileClasses.modalContent],
        }
        : {})}
      buttons={[
        {
          clickHandler: (e) => toggleAudio(audioHelpUrl),
          dontShow: !audioHelpUrl,
          icon: audioIcon,
        },
        {
          clickHandler: closeModal,
          icon: `${imagesURL}/Images/Help/Close50.png`,
        },
      ]}
    >
      <div
        className={mobileRuntime ? mobileClasses.content : undefined}
        style={mobileRuntime ? undefined : { fontSize: '2vmin' }}
        dangerouslySetInnerHTML={{ __html: formattedHtml }}
      />
    </GameModal>
  );
};

ActivityHelp.propTypes = {
  audioSrc: PropTypes.string,
  isPlaying: PropTypes.bool,
  strings: PropTypes.array.isRequired,
  solutionLocale: PropTypes.object,
  activityLocale: PropTypes.object,
  currentActivity: PropTypes.object.isRequired,
  generalTranslate: PropTypes.func.isRequired,
  solutionTranslate: PropTypes.func,
  getStringAudio: PropTypes.func.isRequired,
  versionType: PropTypes.string,
  placementLocaleCode: PropTypes.string,
  placementTranslate: PropTypes.func,
  language: PropTypes.string,
};

const mapStateToProps = ({
  audio,
  game,
  common,
  status,
  flinkPlay: { solutionLocale, activityLocale, solutionTranslate },
}) => ({
  audioSrc: audio.audioSrc,
  isPlaying: audio.isPlaying,
  strings: common.strings,
  solutionLocale,
  activityLocale,
  currentActivity: game.currentActivity,
  generalTranslate: status.translate,
  solutionTranslate,
});

// withGetStringAudio already supports a `language` prop. Supply the same
// version/Placement language that ActivityHelp uses for its text. Otherwise
// its default solutionLocale can be EN even for immersion_vm/immersion_hc.
// No changes to withGetStringAudio, useGetStringAudio, or any other component.
const ActivityHelpWithStringAudio = compose(
  withGetStringAudio,
  connect(mapStateToProps, { closeActivityHelp })
)(ActivityHelp);

const ActivityHelpWithAudioLocale = (props) => {
  const selectedLocale = useSelector((state) => resolveActivityHelpLocale({
    currentActivity: state.game?.currentActivity,
    versionType: props.versionType,
    placementLocaleCode: props.placementLocaleCode,
    activityLocale: state.flinkPlay?.activityLocale,
    solutionLocale: state.flinkPlay?.solutionLocale,
    language: props.language || props.currentLang || props.locale,
  }));

  return (
    <ActivityHelpWithStringAudio {...props} language={selectedLocale} />
  );
};

export default ActivityHelpWithAudioLocale;
